import {
  MOE_CONFIG,
  MOE_EVENTS,
  MOE_MODULE,
  MOE_PROJECT_SOURCE_TYPE,
  MOE_SURFACE,
  MOE_CLONE_TYPE_PROFESSIONAL,
} from '@src/constants/moengage';
import { REQUEST_TYPE } from '@src/constants/request';
import { ENV } from '@src/configs';
import { PROJECT_PRODUCT, PROJECT_TYPE } from '@src/constants/project';
import { VOICE_CLONE_TYPE } from '@src/constants/voiceCloning';
import { VOICE_PROVIDER } from '@src/constants/voice';

// The SDK loads async via GTM; resolves null when blocked (ad-blocker) so every call becomes a no-op.
let sdkPromise = null;
const waitForMoengage = () => {
  if (window.Moengage) return Promise.resolve(window.Moengage);
  if (!sdkPromise) {
    sdkPromise = new Promise((resolve) => {
      const startedAt = Date.now();
      const timer = setInterval(() => {
        if (window.Moengage) {
          clearInterval(timer);
          resolve(window.Moengage);
        } else if (Date.now() - startedAt > MOE_CONFIG.SDK_WAIT_TIMEOUT_MS) {
          clearInterval(timer);
          resolve(null);
        }
      }, MOE_CONFIG.SDK_POLL_INTERVAL_MS);
    });
  }
  return sdkPromise;
};

let currentUserId = null;
let lastIdentitySignature = null;
const sessionOnceKeys = new Set();
// Voice meta is registered at request-accept time — the WS success payload has no voice info.
const requestMetaMap = new Map();

const removeEmptyAttributes = (attrs = {}) =>
  Object.entries(attrs).reduce((acc, [key, value]) => {
    if (value !== undefined && value !== null && value !== '') acc[key] = value;
    return acc;
  }, {});

// Cross-tab dedupe for events every open tab observes; on storage error, track anyway
const checkAndMarkTrackedId = (id) => {
  try {
    const raw = localStorage.getItem(MOE_CONFIG.TRACKED_IDS_STORAGE_KEY);
    const ids = raw ? JSON.parse(raw) : [];
    if (ids.includes(id)) return false;
    ids.push(id);
    localStorage.setItem(
      MOE_CONFIG.TRACKED_IDS_STORAGE_KEY,
      JSON.stringify(ids.slice(-MOE_CONFIG.TRACKED_IDS_MAX)),
    );
    return true;
  } catch (error) {
    return true;
  }
};

const checkAndMarkFirst = (eventName) => {
  try {
    const key = `${MOE_CONFIG.FIRST_FLAG_PREFIX}${eventName}_${
      currentUserId || 'anonymous'
    }`;
    if (localStorage.getItem(key)) return false;
    localStorage.setItem(key, '1');
    return true;
  } catch (error) {
    return false;
  }
};

export const trackEvent = async (
  name,
  attrs = {},
  { withIsFirst = false, oncePerSession = null } = {},
) => {
  try {
    if (oncePerSession) {
      const sessionKey = `${name}:${oncePerSession}`;
      if (sessionOnceKeys.has(sessionKey)) return;
      sessionOnceKeys.add(sessionKey);
    }
    const finalAttrs = removeEmptyAttributes(attrs);
    const moe = await waitForMoengage();
    if (!moe) return;
    // _tracked: first time since tracking was deployed, on this browser
    if (withIsFirst) finalAttrs.is_first_tracked = checkAndMarkFirst(name);
    moe.track_event?.(name, finalAttrs);
  } catch (error) {
    // Tracking must never break the main flow
  }
};

// FNV-1a — LOCKED: changing the algorithm/salt reshuffles every user's bucket.
/* eslint-disable no-bitwise */
export const getSurveyBucket = (userId) => {
  const input = `${userId}${MOE_CONFIG.SURVEY_BUCKET_SALT}`;
  let hash = 2166136261;
  for (let i = 0; i < input.length; i += 1) {
    hash ^= input.charCodeAt(i);
    hash = Math.imul(hash, 16777619);
  }
  return (hash >>> 0) % MOE_CONFIG.SURVEY_BUCKET_SIZE;
};
/* eslint-enable no-bitwise */

// Call on logout — a stale MOE_UID would attribute survey answers to the previous user
export const clearMoengageIdentity = () => {
  try {
    currentUserId = null;
    lastIdentitySignature = null;
    localStorage.removeItem(MOE_CONFIG.UID_STORAGE_KEY);
  } catch (error) {
    // Tracking must never break the main flow
  }
};

// Backend already pushes email/package/credits via s2s — client only fills the gaps.
export const identifyUser = async (user, { locale } = {}) => {
  try {
    if (!user?.id) return;
    currentUserId = user.id;
    localStorage.setItem(MOE_CONFIG.UID_STORAGE_KEY, user.id);
    if (ENV) localStorage.setItem(MOE_CONFIG.ENV_STORAGE_KEY, ENV);
    // Re-renders re-call this — skip if nothing changed (the SDK console.errors on duplicate attributes)
    const signature = `${user.id}:${locale || ''}:${user.ttsVersion || ''}`;
    if (signature === lastIdentitySignature) return;
    lastIdentitySignature = signature;
    const moe = await waitForMoengage();
    if (!moe) return;
    await moe.add_unique_user_id?.(user.id);
    if (user.phoneNumber) moe.add_mobile?.(user.phoneNumber);
    if (user.firstName) moe.add_first_name?.(user.firstName);
    if (user.lastName) moe.add_last_name?.(user.lastName);
    if (user.createdAt)
      moe.add_user_attribute?.('registered_at', user.createdAt);
    if (user.ttsVersion)
      moe.add_user_attribute?.('tts_version', user.ttsVersion);
    if (locale) moe.add_user_attribute?.('locale', locale);
    if (user.isAffiliateMarketer !== undefined)
      moe.add_user_attribute?.(
        'is_affiliate_marketer',
        !!user.isAffiliateMarketer,
      );
    if (user.referral?.systemReferralCode)
      moe.add_user_attribute?.(
        'referral_code',
        user.referral.systemReferralCode,
      );
    moe.add_user_attribute?.('survey_bucket', getSurveyBucket(user.id));
  } catch (error) {
    // no-op
  }
};

export const deriveModule = (request = {}) => {
  if (request.projectId) return MOE_MODULE.PROJECT;
  switch (request.type) {
    case REQUEST_TYPE.API:
    case REQUEST_TYPE.API_CACHING:
      return MOE_MODULE.API;
    case REQUEST_TYPE.DUBBING:
      return MOE_MODULE.SRT;
    default:
      return MOE_MODULE.TTS;
  }
};

// By OUTPUT — text-based TTS projects have no source.type
export const deriveProjectType = (project = {}) => {
  if (project.product === PROJECT_PRODUCT.PRESENTATION)
    return PROJECT_TYPE.PRESENTATION;
  if (project.source?.type === PROJECT_TYPE.VIDEO) return PROJECT_TYPE.VIDEO;
  return PROJECT_TYPE.AUDIO;
};

// Omit the attribute when underivable; callers that know better pass sourceType
const deriveProjectSourceType = ({ product, source, extra }) => {
  if (
    product === PROJECT_PRODUCT.PRESENTATION ||
    source?.type === PROJECT_TYPE.PRESENTATION
  )
    return MOE_PROJECT_SOURCE_TYPE.FILE_PPTX;
  if (source?.type === PROJECT_TYPE.VIDEO)
    return MOE_PROJECT_SOURCE_TYPE.FILE_VIDEO;
  if (source?.type === PROJECT_TYPE.AUDIO)
    return MOE_PROJECT_SOURCE_TYPE.FILE_AUDIO;
  if (extra?.sentences) return MOE_PROJECT_SOURCE_TYPE.FILE_SRT;
  if (source?.url) return MOE_PROJECT_SOURCE_TYPE.URL;
  return undefined;
};

export const trackProjectCreated = ({ product, source, extra, sourceType }) => {
  trackEvent(MOE_EVENTS.PROJECT_CREATED, {
    project_type: deriveProjectType({ product, source }),
    source_type:
      sourceType || deriveProjectSourceType({ product, source, extra }),
  });
};

// Finetune clones may lack `type`
export const checkIsClonedVoice = (voice) =>
  voice?.provider === VOICE_PROVIDER.VBEE_VOICE_CLONING ||
  Object.values(VOICE_CLONE_TYPE).includes(voice?.type);

export const registerRequestMeta = (requestId, { module, voice } = {}) => {
  if (!requestId) return;
  const isClonedVoice = checkIsClonedVoice(voice);
  requestMetaMap.set(requestId, {
    module,
    voiceCode: voice?.code,
    isClonedVoice,
    cloneType:
      voice?.type || (isClonedVoice ? MOE_CLONE_TYPE_PROFESSIONAL : undefined),
  });
};

const takeRequestMeta = (requestId) => {
  const meta = requestMetaMap.get(requestId);
  requestMetaMap.delete(requestId);
  return meta || {};
};

export const trackConvertSuccess = ({
  requestId,
  characters,
  seconds,
  projectId,
}) => {
  // WS broadcasts to every open tab — only the initiating tab has meta; taking it also absorbs re-deliveries
  if (!requestMetaMap.has(requestId)) return;
  const meta = takeRequestMeta(requestId);
  const isCloned = !!meta.isClonedVoice;
  const attrs = {
    request_id: requestId,
    // projectId is not reported, only used to tell a project convert from a TTS one
    module: projectId ? MOE_MODULE.PROJECT : meta.module,
    characters,
    seconds,
    voice_code: meta.voiceCode,
    is_cloned_voice: isCloned,
    clone_type: isCloned ? meta.cloneType : undefined,
  };
  trackEvent(MOE_EVENTS.STUDIO_CONVERT_SUCCESS, attrs, {
    withIsFirst: true,
  });
};

export const trackAudioDownloaded = ({
  requestId,
  module,
  surface,
  count = 1,
}) => {
  trackEvent(
    MOE_EVENTS.AUDIO_DOWNLOADED,
    {
      request_id: requestId,
      module,
      surface,
      count,
    },
    { withIsFirst: true },
  );
};

export const trackBlockAudioDownloaded = ({ requestId }) =>
  trackAudioDownloaded({
    requestId,
    module: MOE_MODULE.PROJECT,
    surface: MOE_SURFACE.PROJECT_BLOCK,
  });

export const trackProjectExported = ({
  projectType,
  exportType,
  requestId,
}) => {
  // Polling callers pass requestId to dedupe across tabs; click paths omit it (each click counts)
  if (requestId && !checkAndMarkTrackedId(`export:${requestId}`)) return;
  trackEvent(
    MOE_EVENTS.PROJECT_EXPORTED,
    {
      project_type: projectType,
      export_type: exportType,
    },
    { withIsFirst: true },
  );
};

export const trackAudioPlayed = ({
  requestId,
  module,
  surface,
  projectType,
  durationSeconds,
  voiceCode,
}) => {
  trackEvent(
    MOE_EVENTS.AUDIO_PLAYED,
    {
      request_id: requestId,
      module,
      surface,
      project_type: projectType,
      duration_seconds: durationSeconds,
      voice_code: voiceCode,
    },
    // Per surface so a full-project play doesn't swallow the block-play event
    { withIsFirst: true, oncePerSession: `${requestId}:${surface || ''}` },
  );
};

// WS broadcast reaches every tab — dedupe per completion (totalCompletedTimes bumps each time)
export const trackOnboardingTaskCompleted = (task) => {
  if (!task?.code) return;
  const dedupeId =
    task.totalCompletedTimes != null
      ? `task:${task.code}:${task.totalCompletedTimes}`
      : null;
  if (dedupeId && !checkAndMarkTrackedId(dedupeId)) return;
  trackEvent(MOE_EVENTS.ONBOARDING_TASK_COMPLETED, {
    task_code: task.code,
  });
};

// is_educator segments downstream analyses (convert, purchase) by educator users
let educatorAttributeSet = false;
export const trackEducatorsHomeViewed = async () => {
  trackEvent(MOE_EVENTS.EDUCATORS_HOME_VIEWED);
  if (educatorAttributeSet) return;
  educatorAttributeSet = true;
  const moe = await waitForMoengage();
  moe?.add_user_attribute?.('is_educator', true);
};

// credits_* only for credit gates, voice_code only for voice gates — others have nothing to report
export const trackPaywallHit = ({
  paywallType,
  module,
  product,
  surface,
  trigger,
  featureKey,
  packageCode,
  creditsNeeded,
  creditsRemaining,
  characters,
  maxCharacters,
  lockedWallet,
  speakers,
  maxSpeakers,
  voiceCode,
}) => {
  trackEvent(
    MOE_EVENTS.PAYWALL_HIT,
    {
      paywall_type: paywallType,
      module,
      product,
      surface,
      trigger,
      feature_key: featureKey,
      package_code: packageCode,
      credits_needed: creditsNeeded,
      credits_remaining: creditsRemaining,
      // Raw text length — with credits_needed shows how much the voice factor inflated the cost
      characters,
      max_characters: maxCharacters,
      locked_wallet: lockedWallet,
      speakers,
      max_speakers: maxSpeakers,
      voice_code: voiceCode,
    },
    { withIsFirst: true },
  );
};

// target_package_code: the package the CTA deep-links to (the offer the user saw)
export const trackPaywallCtaClicked = ({
  paywallType,
  cta,
  packageCode,
  surface,
  featureKey,
  product,
  voiceCode,
  targetPackageCode,
}) => {
  trackEvent(MOE_EVENTS.PAYWALL_CTA_CLICKED, {
    paywall_type: paywallType,
    cta,
    package_code: packageCode,
    surface,
    feature_key: featureKey,
    product,
    voice_code: voiceCode,
    target_package_code: targetPackageCode,
  });
};

// Only non-default selections — resetting a filter is not "using" it
export const trackProjectFilterApplied = ({
  projectType,
  filterType,
  filterValue,
}) => {
  trackEvent(MOE_EVENTS.PROJECT_FILTER_APPLIED, {
    project_type: projectType,
    filter_type: filterType,
    filter_value: filterValue,
  });
};

export const trackFreePlanActivated = ({ previousPackageCode }) => {
  trackEvent(MOE_EVENTS.ACTIVE_FREE_PLAN, {
    is_downgrade: !!previousPackageCode,
    previous_package_code: previousPackageCode,
  });
};
